import { Drawer, Group, Select, Stack, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { DisabledReasonButton, useInvalidSubmitReason } from "@/components/DisabledReason";
import FieldDisplay from "@/components/FieldDisplay";
import { TextWrapper } from "@/components/TextWrapper";

import {
  CLIENT_FLAG_REMOVAL_REASON,
  CLIENT_FLAG_REMOVAL_REASON_OPTIONS,
} from "../constants/client-flags";
import { FLAG_CATEGORY_CODINGS, FLAG_PRIORITY_CODINGS } from "../constants/flags";
import { EMPTY_CELL } from "../constants/ui";
import { ClientFlag } from "../interfaces/client-flags";
import {
  CLIENT_FLAG_REMOVAL_INITIAL_VALUES,
  ClientFlagRemovalFormSchema,
  toClientFlagRemovalReason,
} from "../zod-schemas/client-flag-removal";

import FlagDeprecatedBadge from "./FlagDeprecatedBadge";
import FlagDrawerHeader from "./FlagDrawerHeader";
import FlagPriorityDot from "./FlagPriorityDot";

interface RemoveClientFlagStepProps {
  flag: ClientFlag;
  isRemoving: boolean;
  /** Returns to the drawer's flag list without writing anything. */
  onBack: () => void;
  /** Receives the reason the archive will show: the chosen label, or the text typed for `Other`. */
  onRemove: (reason: string) => void;
}

/**
 * The client flags drawer's Remove Flag step: what is being removed, and why.
 *
 * It takes the drawer's header and body while it is open, so its X goes back to the flag list
 * rather than closing the drawer. `Remove` asks for a reason — and, for `Other`, the clinician's
 * words — before it runs, because the archive keeps that reason as the record of why the flag came
 * off.
 */
export default function RemoveClientFlagStep({
  flag,
  isRemoving,
  onBack,
  onRemove,
}: RemoveClientFlagStepProps) {
  const form = useForm({
    initialValues: CLIENT_FLAG_REMOVAL_INITIAL_VALUES,
    validate: zodResolver(ClientFlagRemovalFormSchema),
  });
  const invalidSubmitReason = useInvalidSubmitReason(form.isValid());

  const isOtherReason = form.values.reason === CLIENT_FLAG_REMOVAL_REASON.Other;

  return (
    <form
      data-testid="remove-client-flag-form"
      onSubmit={form.onSubmit((values) => {
        if (!values.reason) return;
        onRemove(
          toClientFlagRemovalReason({ reason: values.reason, otherReason: values.otherReason }),
        );
      })}
    >
      <FlagDrawerHeader
        onClose={onBack}
        title="Remove Flag"
        closeDisabled={isRemoving}
        closeLabel="Back to flags"
        closeTestId="remove-client-flag-back"
      >
        <DisabledReasonButton
          type="submit"
          color="red.7"
          loading={isRemoving}
          disabledReason={invalidSubmitReason}
          data-testid="remove-client-flag-confirm"
        >
          Remove
        </DisabledReasonButton>
      </FlagDrawerHeader>
      <Drawer.Body p="lg">
        <Stack gap="lg">
          <FieldDisplay title="Flag" value={flag.name} />
          <FieldDisplay title="Priority" value={<FlagPriorityValue flag={flag} />} />
          <FieldDisplay
            title="Category"
            value={flag.category ? FLAG_CATEGORY_CODINGS[flag.category].display : EMPTY_CELL}
          />
          <Select
            withAsterisk
            label="Reason"
            allowDeselect={false}
            placeholder="Select a reason"
            data-testid="remove-client-flag-reason"
            data={CLIENT_FLAG_REMOVAL_REASON_OPTIONS}
            {...form.getInputProps("reason")}
          />
          {isOtherReason && (
            <TextInput
              withAsterisk
              label="Please specify other"
              placeholder="Enter a reason"
              data-testid="remove-client-flag-other-reason"
              {...form.getInputProps("otherReason")}
            />
          )}
        </Stack>
      </Drawer.Body>
    </form>
  );
}

/** Priority dot and label; the `Deprecated` chip once the library entry is gone; `--` if unset. */
function FlagPriorityValue({ flag }: { flag: ClientFlag }) {
  if (flag.isDeprecated) return <FlagDeprecatedBadge />;

  if (!flag.priority) return EMPTY_CELL;

  return (
    <Group gap="xs" wrap="nowrap" component="span">
      <FlagPriorityDot priority={flag.priority} />
      <TextWrapper span>{FLAG_PRIORITY_CODINGS[flag.priority].display}</TextWrapper>
    </Group>
  );
}
